import type { Ref } from 'vue';

import { isHardwareAccelerationActive } from '~/utils';
import { onBeforeUnmount, ref } from 'vue';

export interface UseShortsBackgroundBlurOptions {
	canvasRef: Ref<HTMLCanvasElement | null>;
}

interface RenderState {
	animationId: number | null;
	canvasCtx: CanvasRenderingContext2D | null;
	cachedVideoEl: HTMLVideoElement | null;
	lastDrawTime: number;
	lastVideoSearchTime: number;
	lastVideoTime: number;
	isLowPerformance: boolean;
	forceOpaqueNextDraw: boolean;
}

export function useShortsBackgroundBlur(
	options: UseShortsBackgroundBlurOptions
) {
	const { canvasRef } = options;

	const isLowPerformance = ref(false);
	let state: RenderState | null = null;

	const BG_DRAW_INTERVAL_MS = 66;
	const VIDEO_SEARCH_THROTTLE_MS = 100;
	const BG_CANVAS_SCALE = 0.08;

	function createRenderState(): RenderState {
		return {
			animationId: null,
			canvasCtx: null,
			cachedVideoEl: null,
			lastDrawTime: 0,
			lastVideoSearchTime: 0,
			lastVideoTime: -1,
			isLowPerformance: false,
			forceOpaqueNextDraw: false
		};
	}

	function findActiveVideoElement(): HTMLVideoElement | null {
		if (!state) return null;

		const cached = state.cachedVideoEl;
		if (cached && cached.readyState >= 2 && !cached.paused) {
			return cached;
		}

		const now = performance.now();
		if (now - state.lastVideoSearchTime < VIDEO_SEARCH_THROTTLE_MS) {
			return null;
		}
		state.lastVideoSearchTime = now;

		const container = document.querySelector('[data-reels-modal]');
		if (!container) return null;

		const allVideos = container.querySelectorAll<HTMLVideoElement>('video');
		for (const video of allVideos) {
			if (!video.paused && video.readyState >= 2 && video.videoWidth > 0) {
				state.cachedVideoEl = video;
				return video;
			}
		}
		return null;
	}

	function ensureCanvasDimensions(
		canvas: HTMLCanvasElement,
		targetW: number,
		targetH: number
	): boolean {
		if (!state) return false;

		let isResized = false;
		if (canvas.width !== targetW || canvas.height !== targetH) {
			canvas.width = targetW;
			canvas.height = targetH;
			state.canvasCtx = canvas.getContext('2d');
			isResized = true;
		}

		if (!state.canvasCtx) {
			state.canvasCtx = canvas.getContext('2d');
			isResized = true;
		}

		return isResized;
	}

	function renderHighPerformance(
		timestamp: number,
		canvas: HTMLCanvasElement,
		videoEl: HTMLVideoElement
	) {
		if (!state) return;

		const w = Math.max(1, Math.round(videoEl.videoWidth * BG_CANVAS_SCALE));
		const h = Math.max(1, Math.round(videoEl.videoHeight * BG_CANVAS_SCALE));

		const isFirstDraw = ensureCanvasDimensions(canvas, w, h);
		if (!state.canvasCtx) return;

		// On the first draw (or right after a video switch) paint fully opaque so
		// the new frame replaces any residual content from the previous video.
		// Subsequent draws use a low alpha to blend smoothly.
		const isOpaqueDraw = isFirstDraw || state.forceOpaqueNextDraw;
		state.canvasCtx.globalAlpha = isOpaqueDraw ? 1.0 : 0.15;
		state.canvasCtx.drawImage(videoEl, 0, 0, w, h);
		state.forceOpaqueNextDraw = false;

		state.lastDrawTime = timestamp;
		state.lastVideoTime = videoEl.currentTime;
	}

	function drawBackgroundFrame(timestamp: number) {
		if (!state || state.isLowPerformance) return;

		const canvas = canvasRef.value;
		if (!canvas) {
			state.animationId = requestAnimationFrame(drawBackgroundFrame);
			return;
		}

		const videoEl = findActiveVideoElement();
		if (!videoEl) {
			state.animationId = requestAnimationFrame(drawBackgroundFrame);
			return;
		}

		if (timestamp - state.lastDrawTime < BG_DRAW_INTERVAL_MS) {
			state.animationId = requestAnimationFrame(drawBackgroundFrame);
			return;
		}

		const shouldRender =
			Math.abs(videoEl.currentTime - state.lastVideoTime) > 0.05;
		if (shouldRender) {
			renderHighPerformance(timestamp, canvas, videoEl);
		}

		state.animationId = requestAnimationFrame(drawBackgroundFrame);
	}

	function startAnimation() {
		if (!state) {
			state = createRenderState();
			state.isLowPerformance = !isHardwareAccelerationActive();
			isLowPerformance.value = state.isLowPerformance;
		}
		if (state.isLowPerformance) return;
		if (state.animationId !== null) return;
		state.animationId = requestAnimationFrame(drawBackgroundFrame);
	}

	function stopAnimation() {
		if (state && state.animationId !== null) {
			cancelAnimationFrame(state.animationId);
			state.animationId = null;
		}
	}

	function resetVideoCache() {
		if (!state) return;

		state.cachedVideoEl = null;

		// Clear the canvas right away so the previous video's last frame doesn't
		// linger on screen while the new video is still loading (during which no
		// frame is drawn).
		const canvas = canvasRef.value;
		if (canvas && state.canvasCtx) {
			state.canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
		}

		// Paint the first frame of the new video fully opaque, so it appears at
		// full strength instead of a faint 15% blend over the now-empty canvas.
		state.forceOpaqueNextDraw = true;

		startAnimation();
	}

	onBeforeUnmount(() => {
		stopAnimation();
		state = null;
	});

	return {
		canvasRef,
		startAnimation,
		stopAnimation,
		resetVideoCache,
		isLowPerformance
	};
}
